import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { useBooking } from '../../../contexts/BookingContext';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

const EditBookingPaymentDialog = ({ open, onClose, booking, payment, paymentModes, paymentStatuses }) => {
  const { updateBookingPayment } = useBooking();
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: '',
    payment_date: dayjs(),
    status: 'pending',
    transaction_reference: '',
    notes: '',
    payment_proof: '',
    payment_details: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount,
        payment_method: payment.payment_method,
        payment_date: dayjs(payment.payment_date),
        status: payment.status,
        transaction_reference: payment.transaction_reference || '',
        notes: payment.notes || '',
        payment_proof: payment.payment_proof || '',
        payment_details: payment.payment_details || {}
      });
    }
  }, [payment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? value : value
    }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      payment_date: newDate
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validate transaction reference and payment proof for non-cash payments
    if (formData.payment_method && formData.payment_method !== 'cash') {
      if (!formData.transaction_reference?.trim()) {
        setError('Transaction Reference is required for non-cash payments');
        setLoading(false);
        return;
      }
      if (!formData.payment_proof?.trim()) {
        setError('Payment Proof is required for non-cash payments');
        setLoading(false);
        return;
      }
    }
    
    try {
      // Ensure we send an exact integer value
      const amount = Math.round(Number(formData.amount));
      
      await updateBookingPayment(booking.id, payment.id, {
        amount: amount,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date.format(),
        status: formData.status,
        transaction_reference: formData.transaction_reference || null,
        notes: formData.notes,
        payment_proof: formData.payment_proof || null,
        payment_details: formData.payment_details
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if options are not loaded yet
  if (!paymentModes || !paymentStatuses || paymentModes.length === 0 || paymentStatuses.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Payment</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <ReceiptIcon sx={{ fontSize: 20, color: '#666' }} />
                  Booking ID: {booking?.booking_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="subtitle2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  Payment ID: {payment?.id}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Amount"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            required
            InputProps={{
              inputProps: { 
                min: 0,
                step: 100,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            select
            label="Payment Method"
            name="payment_method"
            value={formData.payment_method}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          >
            {paymentModes && paymentModes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Payment Date"
              value={formData.payment_date}
              onChange={handleDateChange}
              sx={{ width: '100%', mb: 2 }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          >
            {paymentStatuses && paymentStatuses.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Transaction Reference"
            name="transaction_reference"
            value={formData.transaction_reference}
            onChange={handleChange}
            required={formData.payment_method && formData.payment_method !== 'cash'}
            helperText={formData.payment_method === 'cash' ? 'Optional for cash payments' : 'Required for non-cash payments'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Payment Proof URL"
            name="payment_proof"
            value={formData.payment_proof}
            onChange={handleChange}
            required={formData.payment_method && formData.payment_method !== 'cash'}
            helperText={formData.payment_method === 'cash' ? 'Optional for cash payments' : 'Required for non-cash payments'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formData.amount || !formData.payment_method || loading}
            sx={{ position: 'relative' }}
          >
            Update Payment
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px'
                }}
              />
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditBookingPaymentDialog; 